import {
    Collapse, IconButton, Icon, Stack, TableRow, TablePagination,
    Table, TableBody, TableCell, TableContainer, TableHead
} from '@mui/material'

import { styled } from '@mui/material/styles'
import { tableCellClasses } from '@mui/material/TableCell'

import { Fragment, useState, Children, cloneElement, useEffect } from 'react'

import Tile from '@components/Tile'
import theme, { LOADED_THEME } from '@services/theme'

export const CollapsibleTable = (props) => {
    const [openRowId, setOpenRowId] = useState(null);
    const [page, setPage] = useState(props.page ?? 0);
    const [rowsPerPage, setRowsPerPage] = useState(props.rowsPerPage ?? 25);
    const [rowsPerPageOptions, setRowsPerPageOptions] = useState(props.rowsPerPageOptions ?? [25])
    const [itemsCount, setItemsCount] = useState(props.itemsCount ?? 0)

    const StyledTableCell = styled(TableCell)(({ theme }) => ({
        [`&.${tableCellClasses.head}`]: {
            backgroundColor: theme.palette.Datatable.header.background,
            color: theme.palette.Datatable.header.color
        },
        [`&.${tableCellClasses.body}`]: {
            backgroundColor: theme.palette.Datatable.grid.background,
            color: theme.palette.Datatable.row.color,
            border: 0,
            padding: '8px'
        }
    }))

    const CollapsibleChildren = (props) => {
        return Children.map(props.children, (child) => {
            return cloneElement(child, { data: props.data })
        })
    }

    const handleChangePage = (event, newPage) => {
        if (props.onPageChange && props.controlledPagination) props.onPageChange(event, newPage)
        else setPage(newPage);
    }

    const handleChangeRowsPerPage = (event) => {
        if (props.onRowsPerPageChange && props.controlledPagination) {
            handleChangePage(event, 0)
            props.onRowsPerPageChange(+event.target.value)
        }
        else {
            setRowsPerPage(+event.target.value);
            setPage(0);
        }
    }

    useEffect(() => {
        if (props.page > -1) setPage(props.page)
    }, [props.page])

    useEffect(() => {
        if (props.itemsCount) setItemsCount(props.itemsCount)
    }, [props.itemsCount])

    useEffect(() => {
        if (props.rowsPerPageOptions > 0) setRowsPerPageOptions(props.rowsPerPageOptions)
    }, [props.rowsPerPageOptions])

    const CollapsibleRow = ({ row }) => {
        const isOpen = row.id === openRowId

        function handleClick(e) {
            if (row.isCollapseDisabled) {
                e.preventDefault();
                return;
            }

            setOpenRowId(isOpen ? null : row.id)
        }

        return (
            <Fragment key={row.id}>
                <TableRow sx={{ border: `0.5px solid ${theme.palette.Datatable.grid.border}`, '& > *': { borderBottom: 'unset' }, ...(isOpen && { 'borderBottom': 0 }) }}>
                    {props.headers && props.headers.map(header => {
                        const rowData = (header.renderCell && typeof header.renderCell === 'function')
                            ? header.renderCell({ row: row, header: header, value: row[header.field] })
                            : row[header.field]

                        if (header.isFirst) {
                            return (
                                <StyledTableCell key={`cell-${row.id}-${header.field}`}>
                                    <Stack direction={'row'} alignItems={'center'}>
                                        <IconButton
                                            disabled={row.isCollapseDisabled}
                                            onClick={handleClick}
                                        >
                                            {isOpen && !row.isCollapseDisabled ? (<Icon>keyboard_arrow_down</Icon>) : (<Icon>keyboard_arrow_right</Icon>)}
                                        </IconButton>
                                        {rowData}
                                    </Stack>
                                </StyledTableCell>
                            )
                        }
                        return (<StyledTableCell align={header.align || 'left'} key={`cell-${row.id}-${header.field}`} style={header.style || {}}>{rowData}</StyledTableCell>)
                    })}
                </TableRow>
                <TableRow>
                    <StyledTableCell style={{ paddingBottom: 0, paddingTop: 0, ...props.cellStyle }} colSpan={props.headers.length}>
                        <Collapse in={isOpen} timeout={'auto'} unmountOnExit>
                            <CollapsibleChildren data={row.data}>{props.children}</CollapsibleChildren>
                        </Collapse>
                    </StyledTableCell>
                </TableRow>
            </Fragment>
        )
    }

    return (
        <Tile sx={{ ...{ padding: 0, borderRadius: LOADED_THEME.global.radius }, ...props.sx || {} }}>
            <TableContainer sx={{ ...{ borderRadius: LOADED_THEME.global.radius }, ...props.sxGrid || {} }}>
                <Table stickyHeader={props.stickyHeader}>
                    <TableHead>
                        <TableRow>
                            {props.headers && props.headers.map(header => {
                                return (<StyledTableCell align={header.align || 'left'} key={`header-${header.field}`}>{header.headerName}</StyledTableCell>)
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {props.headers && props.headers.length > 0 && props.rows && props.rows.length > 0 && (
                            <>
                                {props.displayFooter && !props.controlledPagination && props.rows
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((row) => (
                                            <CollapsibleRow
                                                key={`row-${row.id}`}
                                                headers={props.headers}
                                                row={row}
                                                openRowId={openRowId}
                                                setOpenRowId={setOpenRowId}
                                                cellStyle={props.cellStyle}
                                            >
                                                {props.children}
                                            </CollapsibleRow>
                                        )
                                    )}
                                {(props.controlledPagination || !props.displayFooter) && props.rows.map((row) => (
                                    <CollapsibleRow
                                        key={`row-${row.id}`}
                                        headers={props.headers}
                                        row={row}
                                        openRowId={openRowId}
                                        setOpenRowId={setOpenRowId}
                                        cellStyle={props.cellStyle}
                                    >
                                        {props.children}
                                    </CollapsibleRow>
                                ))}
                            </>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            {props.displayFooter && (
                <TablePagination
                    rowsPerPageOptions={rowsPerPageOptions}
                    component={'div'}
                    count={itemsCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            )}
        </Tile>
    )
}

export default CollapsibleTable